import { useState } from 'react';
import {
  CreateManualInvestmentRequestBody,
  FinancingDTO,
  InvestmentDTO,
  UpdateRequestBody,
} from '@inversor-hub/types';
import { InvestmentFormData } from '@/features/investments/form/investment-form.schema.ts';
import { handledInvestmentTypeOptions } from '@/features/investments/form/investment-form.schema.ts';
import { formatDateToYYYYMMDD } from '@/utils/misc.ts';
import {
  convertFromMonthly,
  convertToMonthly,
  deriveFinancedPercentage,
  getDetailedMonthlyOpexTotal,
  getDetailedPurchaseCostsTotal,
  hasDetailedMonthlyOpex,
  hasDetailedPurchaseCosts,
} from '@/features/investments/form/utils.ts';

export interface InvestmentFormSubmitData {
  payload: CreateManualInvestmentRequestBody | UpdateRequestBody;
  mainImageFile?: File | null;
  removeExistingMainImage?: boolean;
}

export interface UseInvestmentFormProps {
  initialData?: InvestmentDTO;
  hasRentalHistory?: boolean;
  onSubmit: (data: InvestmentFormSubmitData) => Promise<void>;
  onSuccess?: () => void;
}

const getInitialFinancingValues = (investment?: InvestmentDTO) => {
  if (!investment) {
    return {
      financingDecision: 'PENDING',
      financingInputMode: '',
      monthlyDebtService: null,
      loanAmount: null,
      termYears: null,
      rateType: '',
      fixedRate: null,
      variableSpread: null,
      variableEuribor: null,
    } as const;
  }

  const financing = investment.financing;
  const derivedLoanAmount =
    financing?.loanAmount ??
    (financing?.financedPercentage != null
      ? Math.round((investment.capex?.acquisitionPrice ?? 0) * financing.financedPercentage)
      : null);
  const hasEstimateDetails =
    derivedLoanAmount !== null ||
    financing?.termYears != null ||
    financing?.rateType != null ||
    financing?.fixedRate != null ||
    financing?.variableSpread != null ||
    financing?.variableEuribor != null;
  const hasFinancingDetails = hasEstimateDetails || investment.monthlyDebtService != null;

  return {
    financingDecision: investment.financingDecision ?? (hasFinancingDetails ? 'YES' : 'NO'),
    financingInputMode: hasFinancingDetails
      ? hasEstimateDetails
        ? 'ESTIMATE_PAYMENT'
        : 'MANUAL_PAYMENT'
      : '',
    monthlyDebtService: investment.monthlyDebtService ?? null,
    loanAmount: derivedLoanAmount ?? null,
    termYears: financing?.termYears ?? null,
    rateType: financing?.rateType ?? '',
    fixedRate: financing?.fixedRate ?? null,
    variableSpread: financing?.variableSpread ?? null,
    variableEuribor: financing?.variableEuribor ?? null,
  } as const;
};

export const useInvestmentForm = ({
  initialData,
  hasRentalHistory = false,
  onSubmit,
  onSuccess,
}: UseInvestmentFormProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getInitialFormData = (): InvestmentFormData | undefined => {
    if (!initialData) return;

    const purchaseCostsTotal =
      getDetailedPurchaseCostsTotal({
        itpTax: initialData.capex?.itpTax ?? null,
        notaryFees: initialData.capex?.notaryFees ?? null,
        registryFees: initialData.capex?.registryFees ?? null,
        agencyFees: initialData.capex?.agencyFees ?? null,
        legalFees: initialData.capex?.legalFees ?? null,
        otherTransactionCosts: initialData.capex?.otherTransactionCosts ?? null,
      }) ?? 0;
    const monthlyOpexTotal =
      getDetailedMonthlyOpexTotal({
        ibiTax: convertFromMonthly(initialData.opex?.ibiTax ?? null, true),
        communityFee: initialData.opex?.communityFee ?? null,
        supplyCosts: initialData.opex?.supplyCosts ?? null,
        maintenanceCosts: initialData.opex?.maintenanceCosts ?? null,
        wasteTax: convertFromMonthly(initialData.opex?.wasteTax ?? null, true),
        homeInsurance: convertFromMonthly(initialData.opex?.homeInsurance ?? null, true),
        rentInsurance: convertFromMonthly(initialData.opex?.rentInsurance ?? null, true),
        otherOpex: initialData.opex?.otherOpex ?? null,
      }) ?? 0;

    return {
      alias: initialData.alias || '',
      identificationCode: initialData.identificationCode ?? '',
      country: initialData.country || 'ES',
      address: initialData.address || '',
      city: initialData.city || '',
      postalCode: initialData.postalCode || '',
      province: initialData.province || '',
      type: handledInvestmentTypeOptions.includes(initialData.type as InvestmentFormData['type'])
        ? (initialData.type as InvestmentFormData['type'])
        : 'TRADITIONAL_RENT',
      status: initialData.status,
      purchaseDate: initialData.purchaseDate
        ? formatDateToYYYYMMDD(new Date(initialData.purchaseDate))
        : '',
      ownershipPercentage: initialData.ownershipPercentage ?? 1,
      acquisitionPrice: initialData.capex?.acquisitionPrice ?? null,
      renovationCosts: initialData.capex?.renovationCosts ?? null,
      furnitureEquipmentCosts: initialData.capex?.furnitureEquipmentCosts ?? null,
      initialCostComplete: initialData.capex?.initialCostComplete ?? false,
      purchaseCostsTotal: purchaseCostsTotal > 0 ? purchaseCostsTotal : null,
      itpTax: initialData.capex?.itpTax ?? null,
      notaryFees: initialData.capex?.notaryFees ?? null,
      registryFees: initialData.capex?.registryFees ?? null,
      agencyFees: initialData.capex?.agencyFees ?? null,
      legalFees: initialData.capex?.legalFees ?? null,
      otherTransactionCosts: initialData.capex?.otherTransactionCosts ?? null,
      rentedSince: initialData.rentedSince
        ? formatDateToYYYYMMDD(new Date(initialData.rentedSince))
        : '',
      monthlyRent: initialData.monthlyRent ?? null,
      monthlyOpexTotal: monthlyOpexTotal > 0 ? monthlyOpexTotal : null,
      ibiTax: convertFromMonthly(initialData.opex?.ibiTax ?? null, true),
      communityFee: initialData.opex?.communityFee ?? null,
      supplyCosts: initialData.opex?.supplyCosts ?? null,
      maintenanceCosts: initialData.opex?.maintenanceCosts ?? null,
      wasteTax: convertFromMonthly(initialData.opex?.wasteTax ?? null, true),
      homeInsurance: convertFromMonthly(initialData.opex?.homeInsurance ?? null, true),
      rentInsurance: convertFromMonthly(initialData.opex?.rentInsurance ?? null, true),
      otherOpex: initialData.opex?.otherOpex ?? null,
      ...getInitialFinancingValues(initialData),
      mainImageFile: null,
      removeMainImage: false,
      mainImageUrl: initialData.mainImageUrl,
    };
  };

  const handleSubmit = async (formData: InvestmentFormData) => {
    try {
      setError(null);
      setIsLoading(true);

      const purchaseCostBreakdown = {
        itpTax: formData.itpTax,
        notaryFees: formData.notaryFees,
        registryFees: formData.registryFees,
        agencyFees: formData.agencyFees,
        legalFees: formData.legalFees,
        otherTransactionCosts: formData.otherTransactionCosts,
      };
      const monthlyOpexBreakdown = {
        ibiTax: formData.ibiTax,
        communityFee: formData.communityFee,
        supplyCosts: formData.supplyCosts,
        maintenanceCosts: formData.maintenanceCosts,
        wasteTax: formData.wasteTax,
        homeInsurance: formData.homeInsurance,
        rentInsurance: formData.rentInsurance,
        otherOpex: formData.otherOpex,
      };

      const hasPurchaseBreakdown = hasDetailedPurchaseCosts(purchaseCostBreakdown);
      const hasOpexBreakdown = hasDetailedMonthlyOpex(monthlyOpexBreakdown);
      const acquisitionPrice = formData.acquisitionPrice ?? 0;
      const capex = {
        acquisitionPrice,
        renovationCosts: formData.renovationCosts ?? undefined,
        furnitureEquipmentCosts: formData.furnitureEquipmentCosts ?? undefined,
        initialCostComplete: formData.initialCostComplete,
        itpTax: hasPurchaseBreakdown ? (formData.itpTax ?? undefined) : undefined,
        notaryFees: hasPurchaseBreakdown ? (formData.notaryFees ?? undefined) : undefined,
        registryFees: hasPurchaseBreakdown ? (formData.registryFees ?? undefined) : undefined,
        agencyFees: hasPurchaseBreakdown ? (formData.agencyFees ?? undefined) : undefined,
        legalFees: hasPurchaseBreakdown ? (formData.legalFees ?? undefined) : undefined,
        otherTransactionCosts: hasPurchaseBreakdown
          ? (formData.otherTransactionCosts ?? undefined)
          : (formData.purchaseCostsTotal ?? undefined),
      };

      const financing: FinancingDTO | null | undefined =
        formData.financingDecision === 'YES'
          ? {
              loanAmount:
                formData.financingInputMode === 'ESTIMATE_PAYMENT'
                  ? (formData.loanAmount ?? undefined)
                  : undefined,
              financedPercentage:
                formData.financingInputMode === 'ESTIMATE_PAYMENT'
                  ? deriveFinancedPercentage(formData.loanAmount, formData.acquisitionPrice)
                  : undefined,
              termYears:
                formData.financingInputMode === 'ESTIMATE_PAYMENT'
                  ? (formData.termYears ?? undefined)
                  : undefined,
              rateType:
                formData.financingInputMode === 'ESTIMATE_PAYMENT'
                  ? formData.rateType || undefined
                  : undefined,
              fixedRate:
                formData.financingInputMode === 'ESTIMATE_PAYMENT' && formData.rateType === 'FIXED'
                  ? (formData.fixedRate ?? undefined)
                  : undefined,
              variableSpread:
                formData.financingInputMode === 'ESTIMATE_PAYMENT' &&
                formData.rateType === 'VARIABLE'
                  ? (formData.variableSpread ?? undefined)
                  : undefined,
              variableEuribor:
                formData.financingInputMode === 'ESTIMATE_PAYMENT' &&
                formData.rateType === 'VARIABLE'
                  ? (formData.variableEuribor ?? undefined)
                  : undefined,
            }
          : initialData
            ? null
            : undefined;

      const hasSerializedFinancing =
        financing &&
        Object.values(financing).some((value) => value !== null && value !== undefined);

      const opex =
        hasOpexBreakdown || formData.monthlyOpexTotal !== null
          ? {
              ibiTax: hasOpexBreakdown
                ? (convertToMonthly(formData.ibiTax, true) ?? undefined)
                : undefined,
              communityFee: hasOpexBreakdown
                ? (convertToMonthly(formData.communityFee, false) ?? undefined)
                : undefined,
              wasteTax: hasOpexBreakdown
                ? (convertToMonthly(formData.wasteTax, true) ?? undefined)
                : undefined,
              homeInsurance: hasOpexBreakdown
                ? (convertToMonthly(formData.homeInsurance, true) ?? undefined)
                : undefined,
              rentInsurance: hasOpexBreakdown
                ? (convertToMonthly(formData.rentInsurance, true) ?? undefined)
                : undefined,
              maintenanceCosts: hasOpexBreakdown
                ? (convertToMonthly(formData.maintenanceCosts, false) ?? undefined)
                : undefined,
              supplyCosts: hasOpexBreakdown
                ? (convertToMonthly(formData.supplyCosts, false) ?? undefined)
                : undefined,
              otherOpex: hasOpexBreakdown
                ? (convertToMonthly(formData.otherOpex, false) ?? undefined)
                : (convertToMonthly(formData.monthlyOpexTotal, false) ?? undefined),
            }
          : initialData
            ? null
            : undefined;

      const basePayload = {
        alias: formData.alias.trim(),
        identificationCode: formData.identificationCode.trim() || null,
        country: formData.country.trim().toUpperCase(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        province: formData.province.trim(),
        type: formData.type,
        postalCode: formData.postalCode.trim(),
        status: formData.status,
        purchaseDate: formData.purchaseDate,
        ownershipPercentage: formData.ownershipPercentage ?? 1,
        capex,
        rentedSince:
          hasRentalHistory || formData.type === 'ROOM_RENT'
            ? undefined
            : formData.rentedSince || undefined,
        monthlyRent:
          hasRentalHistory || formData.type === 'ROOM_RENT'
            ? undefined
            : (formData.monthlyRent ?? undefined),
        opex,
        financing: hasSerializedFinancing ? financing : initialData ? null : undefined,
        financingDecision: formData.financingDecision,
        monthlyDebtService:
          formData.financingDecision === 'YES' && formData.financingInputMode === 'MANUAL_PAYMENT'
            ? (formData.monthlyDebtService ?? undefined)
            : formData.financingDecision === 'NO' && initialData
              ? null
              : undefined,
      };

      const payload: CreateManualInvestmentRequestBody | UpdateRequestBody = initialData
        ? ({
            ...basePayload,
            rentedSince:
              hasRentalHistory || formData.type === 'ROOM_RENT'
                ? undefined
                : formData.rentedSince || null,
            monthlyRent:
              hasRentalHistory || formData.type === 'ROOM_RENT' ? undefined : formData.monthlyRent,
            opex,
            financing: hasSerializedFinancing ? financing : null,
            monthlyDebtService:
              formData.financingDecision === 'YES' &&
              formData.financingInputMode === 'MANUAL_PAYMENT'
                ? formData.monthlyDebtService
                : formData.financingDecision === 'NO'
                  ? null
                  : undefined,
          } as UpdateRequestBody)
        : ({
            ...basePayload,
            opex: opex ?? undefined,
            financing: hasSerializedFinancing ? (financing ?? undefined) : undefined,
            monthlyDebtService:
              formData.financingDecision === 'YES' &&
              formData.financingInputMode === 'MANUAL_PAYMENT'
                ? (formData.monthlyDebtService ?? undefined)
                : undefined,
          } as CreateManualInvestmentRequestBody);

      await onSubmit({
        payload,
        mainImageFile: formData.mainImageFile ?? undefined,
        removeExistingMainImage: Boolean(formData.removeMainImage && initialData?.mainImageUrl),
      });
      onSuccess?.();
    } catch (_error) {
      setError('Error al procesar la inversión. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  return {
    initialFormData: getInitialFormData(),
    handleSubmit,
    handleErrorDismiss,
    error,
    isLoading,
  };
};
